<template>
	<div class="flex flex-col lg:flex-row items-end justify-between gap-g2m sm:gap-g1m" ref="sliderContainer">
		<div class="relative w-full lg:w-g5p des:w-g7p flex flex-col items-start justify-between" ref="sliderText">
			<slot name="text" :currentWord="currentWord"></slot>
			<slot name="button"></slot>
		</div>
		<div class="relative w-g10p h-g8p sm:h-g6p md:h-g4p lg:w-g4p lg:h-g3p flex-shrink-0">
			<div class="absolute inset-0 grid-graphic grid-hash-marks grid-hash-marks-fixed grid-cols-5 grid-rows-4 sm:grid-rows-3 md:grid-cols-10 md:grid-rows-4 lg:grid-cols-4 lg:grid-rows-3"></div>
			<div class="absolute inset-0 *:absolute *:inset-0 overflow-hidden">
				<TransitionGroup
					enter-from-class="opacity-0 translate-x-[-100%]"
					leave-to-class="opacity-0 translate-x-[100%]"
					enter-active-class="transition-all duration-1000 ease-in-out"
					leave-active-class="transition-all duration-1000 ease-in-out"
				>
					<slot name="slides" :currentIndex="currentIndex"></slot>
				</TransitionGroup>
			</div>
			<div class="absolute top-11 left-16 font-serif text-14 italic opacity-50">Fig. {{ currentIndex + 1 }}</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { onResize } from "@js/throttle.js"
import { getGridHeight } from "@js/grid.js"

const twConfig = __TAILWIND_CONFIG__;

const props = defineProps({
	words: {
		type: Array,
		required: true,
		validator: (value) => value.every(item => typeof item === 'string')
	},
	interval: {
		type: Number,
		default: 5000
	}
})

const emit = defineEmits(['advance']);

const currentIndex = ref(0)
const timer = ref(null)
const progressTimer = ref(null)
const sliderContainer = ref(null)
const sliderText = ref(null)
const progress = ref(0)

const currentWord = computed(() => props.words[currentIndex.value] || '')

const updateProgress = () => {
	const startTime = Date.now()
	const updateProgressBar = () => {
		const elapsed = Date.now() - startTime
		progress.value = (elapsed / props.interval) * 100

		if (sliderContainer.value) {
			sliderContainer.value.style.setProperty('--progress', `${progress.value}%`)
		}

		if (progress.value < 100) {
			progressTimer.value = requestAnimationFrame(updateProgressBar)
		}
	}

	if (progressTimer.value) {
		cancelAnimationFrame(progressTimer.value)
	}

	progress.value = 0
	if (sliderContainer.value) {
		sliderContainer.value.style.setProperty('--progress', '0%')
	}
	progressTimer.value = requestAnimationFrame(updateProgressBar)
}

const advance = () => {
	currentIndex.value = (currentIndex.value + 1) % props.words.length
	updateProgress()
	nextTick(() => {
		emit('advance')
	})
}

const startTimer = () => {
	timer.value = setInterval(advance, props.interval)
	updateProgress()
}

const stopTimer = () => {
	if (timer.value) {
		clearInterval(timer.value)
		timer.value = null
	}
	if (progressTimer.value) {
		cancelAnimationFrame(progressTimer.value)
		progressTimer.value = null
	}
}

const setContainerTopPadding = async () => {
	// pt-[calc(mod((100vh_-_(100vh_/_var(--grid-size-unitless))),_var(--grid-size)_*_2))]

	// Get the grid size from CSS variable
	const div = document.createElement('div');
	div.style.width = `var(--grid-size)`;
	div.style.flexShrink = 0;
	sliderContainer.value.appendChild(div);
	const gridSize = div.getBoundingClientRect().width;
	sliderContainer.value.removeChild(div);

	sliderContainer.value.style.paddingTop = "0px";
	const containerHeight = sliderContainer.value.getBoundingClientRect().height;

	const style = window.getComputedStyle(sliderContainer.value.parentElement);
	const height = sliderContainer.value.parentElement.getBoundingClientRect().height;
	const paddingTop = parseFloat(style.paddingTop);
	const paddingBottom = parseFloat(style.paddingBottom);
	const heightExcludingPadding = height - paddingTop - paddingBottom;
	// let remainder = (heightExcludingPadding - (heightExcludingPadding / gridSize)) % (gridSize * 2);
	let availableSpace = heightExcludingPadding - containerHeight;
	let remainder = (availableSpace - (availableSpace / gridSize)) % (gridSize * 2);

	sliderContainer.value.style.paddingTop = `${remainder}px`;
}

const setTextHeight = async () => {
	sliderText.value.style.removeProperty('height');
	let type = '';
	const nearestHeader = sliderContainer.value.closest('header');
	await nextTick();

	if (window.matchMedia(`(min-width: ${twConfig.theme.screens.sm}) and (max-width: ${twConfig.theme.screens.md})`).matches) {
		type = 'even';
	} else if (nearestHeader && getComputedStyle(nearestHeader).position === 'relative') {
		type = ''
	} else {
		type = 'odd';
	}

	sliderText.value.style.setProperty('height', `${getGridHeight(sliderText.value, type)}px`);;
}

onMounted(async () => {
	startTimer()
	setContainerTopPadding()
	setTextHeight()
	onResize(() => {
		setContainerTopPadding()
		setTextHeight()
	})
})

onBeforeUnmount(() => {
	stopTimer()
})
</script>

<style>
.slide-progress {
	@apply relative inline-block;

	&::before {
		content: "";
		@apply absolute inset-x-0 bottom-4;
		@apply h-2 bg-sand-700;
	}

	&::after {
		content: "";
		@apply absolute inset-x-0 bottom-4;
		@apply h-2 bg-red-500;

		width: var(--progress, 0%);
	}
}
</style>
